/* eslint-disable react/prop-types */
import classNames from 'classnames';
import React, { useCallback } from 'react';
import { Form } from 'react-bootstrap';
import {
  useTable,
  useSortBy,
  usePagination,
  useRowSelect,
  useGlobalFilter
} from 'react-table';
import y from 'y4/Utils';

export const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, className, ...rest }, ref) => {
    const defaultRef = React.useRef();

    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <Form.Check
        type="checkbox"
        className={classNames('form-check fs-0 mb-0', className)}
      >
        <Form.Check.Input type="checkbox" ref={resolvedRef} {...rest} />
      </Form.Check>
    );
  }
);

const AdvanceTableWrapper = ({
  cacheId,
  children,
  columns,
  data,
  sortable,
  selection,
  selectionColumnWidth,
  selectionChanged,
  pagination,
  selectedIds = {},
  perPage = 10,
  atPage = 0,
  topFilter = '',
  sort = []
}) => {
  //idUi or default
  const getRowId = useCallback(
    (row, relativeIndex, parent) => {
      if (row.idUi) return row.idUi;
      if (row.id) return row.id;
      return parent ? [parent.id, relativeIndex].join('.') : relativeIndex;
    },
    [columns, data]
  );

  //Init info from import info
  let lastSelectedIds = selectedIds;
  let lastPageSize = perPage;
  let lastPageIndex = atPage;
  let lastGlobalFilter = topFilter;
  let lastSortBy = sort;

  //Read state from session
  if (cacheId) {
    const state_json = y.getSessionItem(`${cacheId}_atw`);
    if (state_json) {
      const state = JSON.parse(state_json);
      if (state) {
        lastSelectedIds = state.selectedRowIds;
        lastPageSize = state.pageSize;
        lastPageIndex = state.pageIndex;
        lastGlobalFilter = state.globalFilter;
        lastSortBy = state.sortBy;
      }
    }
  }

  const get_page_index = (to, data, ps) => {
    if (!to) return 0;
    if (!data) return 0;
    if (!data.length) return 0;
    if (!ps) return 0;
    const v = y.min(to, Math.floor(data.length / ps));
    return v;
  };

  let selected_counter = 0;

  const {
    getTableProps,
    headers,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    gotoPage,
    selectedFlatRows,
    pageCount,
    state: { pageIndex, pageSize, selectedRowIds, globalFilter, sortBy },
    setGlobalFilter
  } = useTable(
    {
      columns,
      data,
      disableSortBy: !sortable,
      initialState: {
        pageSize: pagination ? lastPageSize : data.length,
        pageIndex: get_page_index(lastPageIndex, data, lastPageSize),
        globalFilter: lastGlobalFilter,
        sortBy: lastSortBy,
        selectedRowIds: lastSelectedIds ?? {}
      },
      getRowId
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      if (selection) {
        hooks.visibleColumns.push(columns => [
          {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            headerProps: {
              style: {
                width: selectionColumnWidth
              }
            },
            cellProps: {
              style: {
                width: selectionColumnWidth
              }
            },
            Cell: ({ row }) => {
              const trsps = { ...row.getToggleRowSelectedProps() };
              trsps.onChange = () => {
                row.toggleRowSelected(!row.isSelected);
                row.original['SELECTED_ORDER_INDEX'] = ++selected_counter;
              };
              return (
                <div>
                  <IndeterminateCheckbox {...trsps} />
                </div>
              );
            }
          },
          ...columns
        ]);
      }
    }
  );

  if (selection) {
    React.useEffect(() => {
      if (selectionChanged)
        selectionChanged(selectedFlatRows.map(d => d.original));
    }, [selectedFlatRows]);
  }

  if (cacheId) {
    React.useEffect(() => {
      y.setSessionItem(
        `${cacheId}_atw`,
        JSON.stringify({
          pageIndex,
          pageSize,
          selectedRowIds,
          globalFilter,
          sortBy
        })
      );
    }, [pageIndex, pageSize, selectedRowIds, globalFilter]);
  }
  const recursiveMap = children => {
    return React.Children.map(children, child => {
      if (child.props?.children) {
        return React.cloneElement(child, {
          children: recursiveMap(child.props.children)
        });
      } else {
        if (child.props?.table) {
          return React.cloneElement(child, {
            ...child.props,
            getTableProps,
            headers,
            page,
            prepareRow,
            canPreviousPage,
            canNextPage,
            nextPage,
            previousPage,
            gotoPage,
            pageCount,
            pageIndex,
            selectedRowIds,
            pageSize,
            setPageSize,
            globalFilter,
            setGlobalFilter
          });
        } else {
          return child;
        }
      }
    });
  };

  return <>{recursiveMap(children)}</>;
};

export default AdvanceTableWrapper;
